import type { RadioStation } from './radios';
import type { HostProfile } from './hostProfile';
import type { ProgramProfile } from './programProfile';
import type { GlobalEvent } from './globalEvents';
import type { Contest } from './adminContests';

// ─── Tipos ───────────────────────────────────────────

export type ReactionType = 'like' | 'love' | 'fire' | 'mic' | 'music' | 'clap';
export type EntityType = 'radio' | 'program' | 'host' | 'event' | 'contest' | 'post' | 'photo' | 'video' | 'comment' | 'club';

export interface ReactionCounts {
  like: number;
  love: number;
  fire: number;
  mic: number;
  music: number;
  clap: number;
}

export interface SocialStats {
  entityId: string | number;
  entityType: EntityType;
  entityName: string;
  likes: number;
  reactions: ReactionCounts;
  shares: number;
  saves: number;
  reach: number;
  interactions: number;
  trendDirection: 'up' | 'down' | 'stable';
  trendPercent: number;
  isTrending: boolean;
  lastUpdated: string;
}

export interface ViralEntry {
  entityId: string | number;
  entityType: EntityType;
  entityName: string;
  entitySubtitle: string;
  entityImageUrl: string;
  entityLink: string;
  shares: number;
  reach: number;
  interactions: number;
  trendPercent: number;
  ranking: number;
}

// ─── Reaction config ──────────────────────────────────

export const reactionConfig: Record<ReactionType, { emoji: string; icon: string; label: string; color: string }> = {
  like:    { emoji: '👍', icon: 'ri-thumb-up-line',     label: 'Me gusta',  color: 'text-sky-500' },
  love:    { emoji: '❤️', icon: 'ri-heart-line',         label: 'Me encanta', color: 'text-red-500' },
  fire:    { emoji: '🔥', icon: 'ri-fire-line',          label: 'Trending',  color: 'text-orange-500' },
  mic:     { emoji: '🎙️', icon: 'ri-mic-line',           label: 'Gran voz',  color: 'text-amber-500' },
  music:   { emoji: '🎵', icon: 'ri-music-line',         label: 'Temazo',    color: 'text-emerald-500' },
  clap:    { emoji: '👏', icon: 'ri-hand-heart-line',     label: 'Aplausos',  color: 'text-violet-500' },
};

// ─── Generadores de SocialStats ────────────────────────

function randomReactions(): ReactionCounts {
  const base = Math.floor(Math.random() * 2000) + 200;
  return {
    like: base + Math.floor(Math.random() * 800),
    love: Math.floor(base * 0.6) + Math.floor(Math.random() * 400),
    fire: Math.floor(base * 0.3) + Math.floor(Math.random() * 200),
    mic: Math.floor(base * 0.2) + Math.floor(Math.random() * 150),
    music: Math.floor(base * 0.15) + Math.floor(Math.random() * 100),
    clap: Math.floor(base * 0.25) + Math.floor(Math.random() * 180),
  };
}

function makeSocialStats(
  entityId: string | number,
  entityType: EntityType,
  entityName: string,
  opts?: { scale?: number; trending?: boolean },
): SocialStats {
  const scale = opts?.scale || 1;
  const reactions = randomReactions();
  const totalReactions = Object.values(reactions).reduce((a, b) => a + b, 0);
  const likes = reactions.like + reactions.love;
  const shares = Math.floor((totalReactions * 0.2) * scale) + Math.floor(Math.random() * 500 * scale);
  const saves = Math.floor(shares * 0.4) + Math.floor(Math.random() * 200);
  const reach = likes * 8 + shares * 15 + Math.floor(Math.random() * 10000 * scale);
  const interactions = totalReactions + shares + saves;
  const trendPercent = opts?.trending ? Math.floor(Math.random() * 45) + 15 : Math.floor(Math.random() * 25) - 10;
  const trendDirection = trendPercent > 5 ? 'up' : trendPercent < -5 ? 'down' : 'stable';
  return {
    entityId,
    entityType,
    entityName,
    likes,
    reactions,
    shares,
    saves,
    reach,
    interactions,
    trendDirection,
    trendPercent,
    isTrending: opts?.trending || trendPercent > 15,
    lastUpdated: new Date().toISOString(),
  };
}

// ─── Datos ────────────────────────────────────────────

export const radioSocialStats: SocialStats[] = [
  makeSocialStats('radio-1', 'radio', 'La Mexicana FM', { scale: 3, trending: true }),
  makeSocialStats('radio-2', 'radio', 'Radio Éxito FM', { scale: 2.5, trending: true }),
  makeSocialStats('radio-3', 'radio', 'Radio Ritmo', { scale: 2.2, trending: true }),
  makeSocialStats('radio-4', 'radio', 'Cadena Norte', { scale: 2 }),
  makeSocialStats('radio-5', 'radio', 'Estación Central', { scale: 1.8 }),
  makeSocialStats('radio-6', 'radio', 'Radio Uno', { scale: 1.6 }),
  makeSocialStats('radio-7', 'radio', 'Onda Digital', { scale: 1.5, trending: true }),
  makeSocialStats('radio-8', 'radio', 'Frecuencia Pop', { scale: 1.4 }),
  makeSocialStats('radio-9', 'radio', 'Radio Sol', { scale: 1.2 }),
  makeSocialStats('radio-10', 'radio', 'La Radio de Todos', { scale: 1.1 }),
  makeSocialStats('radio-11', 'radio', 'Mega Hits', { scale: 1.05 }),
  makeSocialStats('radio-12', 'radio', 'Radio Clásica', { scale: 1 }),
];

export const programSocialStats: SocialStats[] = [
  makeSocialStats('program-1', 'program', 'Mañanas Argentinas', { scale: 2.8, trending: true }),
  makeSocialStats('program-2', 'program', 'Tardes con Vale', { scale: 2.5, trending: true }),
  makeSocialStats('program-3', 'program', 'Deporte Total', { scale: 2.2 }),
  makeSocialStats('program-4', 'program', 'Modo Creativo', { scale: 1.8 }),
  makeSocialStats('program-5', 'program', 'Onda Nocturna', { scale: 2, trending: true }),
];

export const hostSocialStats: SocialStats[] = [
  makeSocialStats('host-1', 'host', 'Valentina Ríos', { scale: 3.2, trending: true }),
  makeSocialStats('host-2', 'host', 'Roberto Aguilar', { scale: 2.8 }),
  makeSocialStats('host-3', 'host', 'Andrés Castillo', { scale: 2.5, trending: true }),
  makeSocialStats('host-4', 'host', 'Camila Ortega', { scale: 2.2 }),
  makeSocialStats('host-5', 'host', 'Diego Montenegro', { scale: 2 }),
  makeSocialStats('host-6', 'host', 'Sofía Linares', { scale: 1.8, trending: true }),
  makeSocialStats('host-7', 'host', 'Javier Solís', { scale: 1.6 }),
];

export const eventSocialStats: SocialStats[] = [
  makeSocialStats('event-1', 'event', 'Fiesta de la Radio 2026', { scale: 2, trending: true }),
  makeSocialStats('event-2', 'event', 'Concierto en Vivo', { scale: 1.8 }),
  makeSocialStats('event-3', 'event', 'Maratón Musical Solidaria', { scale: 1.6, trending: true }),
  makeSocialStats('event-4', 'event', 'Encuentro de Locutores', { scale: 1.5 }),
  makeSocialStats('event-5', 'event', 'Festival de Verano', { scale: 1.4 }),
];

export const contestSocialStats: SocialStats[] = [
  makeSocialStats('contest-1', 'contest', 'Concurso de Talentos RFW', { scale: 1.8, trending: true }),
  makeSocialStats('contest-2', 'contest', 'Adivina la Canción', { scale: 1.5 }),
  makeSocialStats('contest-3', 'contest', 'Trivia Musical', { scale: 1.4 }),
  makeSocialStats('contest-4', 'contest', 'Mejor Fan del Mes', { scale: 1.3, trending: true }),
  makeSocialStats('contest-5', 'contest', 'Sorteo de Entradas', { scale: 1.6 }),
];

// ─── Viralidad ────────────────────────────────────────

export const trendingNow: ViralEntry[] = [
  {
    entityId: 'radio-1', entityType: 'radio', entityName: 'La Mexicana FM',
    entitySubtitle: 'Regional Mexicano · 94.5 FM', entityImageUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20broadcast%20studio%20with%20professional%20microphone%20and%20audio%20mixing%20console%2C%20warm%20ambient%20lighting%20with%20orange%20and%20amber%20tones%2C%20modern%20radio%20production%20environment%2C%20dynamic%20energy%2C%20clean%20professional%20composition&width=600&height=400&seq=rviral-01&orientation=landscape',
    entityLink: '/radio/94.5', shares: 38420, reach: 892400, interactions: 156300, trendPercent: 42, ranking: 1,
  },
  {
    entityId: 'host-1', entityType: 'host', entityName: 'Valentina Ríos',
    entitySubtitle: 'Locutora · La Mexicana FM', entityImageUrl: 'https://readdy.ai/api/search-image?query=Professional%20female%20radio%20host%20in%20modern%20broadcast%20studio%20with%20headphones%2C%20passionate%20expression%20while%20speaking%20into%20microphone%2C%20warm%20studio%20lighting%2C%20portrait%20style%2C%20authentic%20media%20professional%20atmosphere&width=600&height=400&seq=rviral-02&orientation=landscape',
    entityLink: '/host/valentina-rios', shares: 28900, reach: 671200, interactions: 124800, trendPercent: 38, ranking: 2,
  },
  {
    entityId: 'program-2', entityType: 'program', entityName: 'Tardes con Vale',
    entitySubtitle: 'Entretenimiento · L-V 15:00', entityImageUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20afternoon%20radio%20talk%20show%20studio%20scene%2C%20colorful%20set%20design%20with%20comfortable%20seating%20area%2C%20professional%20broadcast%20equipment%2C%20warm%20inviting%20atmosphere%2C%20lifestyle%20entertainment%20production&width=600&height=400&seq=rviral-03&orientation=landscape',
    entityLink: '/program/tardes-con-vale', shares: 25100, reach: 583400, interactions: 112600, trendPercent: 35, ranking: 3,
  },
  {
    entityId: 'radio-7', entityType: 'radio', entityName: 'Onda Digital',
    entitySubtitle: 'Electrónica · 107.3 FM', entityImageUrl: 'https://readdy.ai/api/search-image?query=Futuristic%20electronic%20music%20radio%20studio%20with%20neon%20lighting%20and%20digital%20audio%20workstations%2C%20modern%20DJ%20equipment%20setup%2C%20cyberpunk%20aesthetic%20meets%20professional%20broadcast%2C%20energetic%20atmosphere&width=600&height=400&seq=rviral-04&orientation=landscape',
    entityLink: '/radio/107.3', shares: 19800, reach: 445100, interactions: 98700, trendPercent: 31, ranking: 4,
  },
  {
    entityId: 'event-1', entityType: 'event', entityName: 'Fiesta de la Radio 2026',
    entitySubtitle: 'Evento Masivo · CDMX', entityImageUrl: 'https://readdy.ai/api/search-image?query=Massive%20outdoor%20radio%20festival%20event%20scene%2C%20colorful%20concert%20stage%20with%20spectacular%20lighting%20rig%2C%20huge%20energetic%20crowd%20with%20raised%20hands%2C%20warm%20golden%20sunset%20atmosphere%2C%20wide%20angle%20dynamic%20event%20photography&width=600&height=400&seq=rviral-05&orientation=landscape',
    entityLink: '/events', shares: 17200, reach: 392800, interactions: 87400, trendPercent: 28, ranking: 5,
  },
  {
    entityId: 'host-3', entityType: 'host', entityName: 'Andrés Castillo',
    entitySubtitle: 'Locutor Deportivo · Cadena Norte', entityImageUrl: 'https://readdy.ai/api/search-image?query=Energetic%20male%20sports%20radio%20commentator%20in%20broadcast%20booth%20with%20live%20game%20visible%20through%20window%2C%20passionate%20delivery%20into%20microphone%2C%20sports%20journalism%20professional%20atmosphere&width=600&height=400&seq=rviral-06&orientation=landscape',
    entityLink: '/host/andres-castillo', shares: 15300, reach: 348900, interactions: 79200, trendPercent: 24, ranking: 6,
  },
  {
    entityId: 'contest-1', entityType: 'contest', entityName: 'Concurso de Talentos RFW',
    entitySubtitle: 'Gran Final · Premio $50K', entityImageUrl: 'https://readdy.ai/api/search-image?query=Talent%20show%20competition%20stage%20with%20golden%20spotlight%20and%20trophy%20display%2C%20excited%20contestants%20on%20stage%2C%20dramatic%20theatrical%20lighting%2C%20grand%20finale%20atmosphere%2C%20entertainment%20competition%20event&width=600&height=400&seq=rviral-07&orientation=landscape',
    entityLink: '/competencias', shares: 14100, reach: 315600, interactions: 71800, trendPercent: 22, ranking: 7,
  },
  {
    entityId: 'program-5', entityType: 'program', entityName: 'Onda Nocturna',
    entitySubtitle: 'Música · L-D 22:00', entityImageUrl: 'https://readdy.ai/api/search-image?query=Late%20night%20radio%20show%20studio%20with%20moody%20atmospheric%20lighting%2C%20deep%20blue%20and%20purple%20ambient%20glow%2C%20intimate%20broadcast%20booth%20setting%2C%20sophisticated%20nighttime%20radio%20production%20aesthetic&width=600&height=400&seq=rviral-08&orientation=landscape',
    entityLink: '/program/onda-nocturna', shares: 12800, reach: 287300, interactions: 65400, trendPercent: 19, ranking: 8,
  },
];

export const mostSharedToday: ViralEntry[] = [
  trendingNow[0], trendingNow[1], trendingNow[3], trendingNow[2], trendingNow[4],
];

// ─── Helpers ───────────────────────────────────────────

export function getSocialStats(entityType: EntityType, entityId?: string | number): SocialStats | undefined {
  const map: Record<EntityType, SocialStats[]> = {
    radio: radioSocialStats,
    program: programSocialStats,
    host: hostSocialStats,
    event: eventSocialStats,
    contest: contestSocialStats,
    post: [],
    photo: [],
    video: [],
    comment: [],
    club: [],
  };
  const list = map[entityType] || [];
  if (entityId !== undefined) return list.find((s) => String(s.entityId) === String(entityId));
  return list[0];
}

export function getTotalReactions(stats: SocialStats): number {
  return Object.values(stats.reactions).reduce((a, b) => a + b, 0);
}

export function getTopReaction(stats: SocialStats): ReactionType {
  const r = stats.reactions;
  const entries = Object.entries(r) as [ReactionType, number][];
  return entries.sort((a, b) => b[1] - a[1])[0][0];
}

export function getTrendingNow(): ViralEntry[] {
  return [...trendingNow].sort((a, b) => b.trendPercent - a.trendPercent);
}

export function getMostShared(): ViralEntry[] {
  return [...trendingNow].sort((a, b) => b.shares - a.shares).slice(0, 8);
}

export function formatSocialCount(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

export function formatReachCount(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return n.toString();
}

// ─── Mention helpers ──────────────────────────────────

export interface MentionSuggestion {
  type: EntityType;
  id: string;
  name: string;
  subtitle: string;
  imageUrl: string;
  link: string;
}

export const mentionSuggestions: MentionSuggestion[] = [
  { type: 'radio', id: 'radio-1', name: 'La Mexicana FM', subtitle: '94.5 FM', imageUrl: 'https://readdy.ai/api/search-image?query=Mexican%20radio%20station%20logo%20design%2C%20vibrant%20colors%2C%20modern%20circular%20emblem%2C%20FM%20frequency%20branding%2C%20professional%20music%20radio%20identity&width=100&height=100&seq=mention-r1&orientation=squarish', link: '/radio/94.5' },
  { type: 'radio', id: 'radio-2', name: 'Radio Éxito FM', subtitle: '88.9 FM', imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20circular%20logo%20with%20musical%20notes%20and%20frequency%20display%2C%20bold%20professional%20design%2C%20vibrant%20color%20palette%2C%20broadcast%20identity%20emblem&width=100&height=100&seq=mention-r2&orientation=squarish', link: '/radio/88.9' },
  { type: 'radio', id: 'radio-3', name: 'Radio Ritmo', subtitle: '102.1 FM', imageUrl: 'https://readdy.ai/api/search-image?query=Rhythm%20music%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20soundwave%20elements%2C%20energetic%20visual%20identity%2C%20modern%20FM%20radio%20branding&width=100&height=100&seq=mention-r3&orientation=squarish', link: '/radio/102.1' },
  { type: 'program', id: 'program-1', name: 'Mañanas Argentinas', subtitle: 'Programa', imageUrl: 'https://readdy.ai/api/search-image?query=Morning%20show%20program%20circular%20logo%20badge%2C%20sunrise%20colors%20and%20coffee%20cup%20silhouette%2C%20bright%20energetic%20brand%20identity%2C%20radio%20talk%20show%20emblem&width=100&height=100&seq=mention-p1&orientation=squarish', link: '/program/mananas-argentinas' },
  { type: 'program', id: 'program-2', name: 'Tardes con Vale', subtitle: 'Programa', imageUrl: 'https://readdy.ai/api/search-image?query=Afternoon%20entertainment%20show%20circular%20brand%20badge%2C%20warm%20sunset%20colors%2C%20fun%20casual%20radio%20program%20identity%20emblem%2C%20lifestyle%20content%20logo&width=100&height=100&seq=mention-p2&orientation=squarish', link: '/program/tardes-con-vale' },
  { type: 'host', id: 'host-1', name: 'Valentina Ríos', subtitle: 'Locutora', imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20female%20radio%20host%20portrait%20for%20social%20media%20avatar%2C%20warm%20friendly%20expression%2C%20modern%20broadcast%20personality%20headshot%2C%20clean%20neutral%20background&width=100&height=100&seq=mention-h1&orientation=squarish', link: '/host/valentina-rios' },
  { type: 'host', id: 'host-2', name: 'Roberto Aguilar', subtitle: 'Locutor', imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20male%20radio%20host%20portrait%20for%20social%20media%20avatar%2C%20confident%20friendly%20expression%2C%20broadcast%20personality%20headshot%2C%20clean%20neutral%20studio%20background&width=100&height=100&seq=mention-h2&orientation=squarish', link: '/host/roberto-aguilar' },
];

export function searchMentions(query: string): MentionSuggestion[] {
  if (!query || query.length < 2) return [];
  const q = query.toLowerCase();
  return mentionSuggestions.filter((m) => m.name.toLowerCase().includes(q));
}